import algoliasearch from 'algoliasearch/lite';
import getConfig from 'next/config';
import { TranslationKey } from './data-contract/auto-generated/translation-model';
import qs from 'qs';
import { useFrame } from '~/shared/utils';
import { useMemo } from 'react';

const { publicRuntimeConfig } = getConfig() || {};

export const ALGOLIA_APP = publicRuntimeConfig?.NEXT_PUBLIC_ALGOLIA_APP;
export const ALGOLIA_TOKEN = publicRuntimeConfig?.NEXT_PUBLIC_ALGOLIA_TOKEN;
export const ALGOLIA_INDEX: string = publicRuntimeConfig?.NEXT_PUBLIC_ALGOLIA_INDEX;
export const INDEX_SORT_PRICE_ASC: string =
    publicRuntimeConfig?.NEXT_PUBLIC_ALGOLIA_INDEX_PRICE_ASC;
export const INDEX_SORT_PRICE_DESC: string =
    publicRuntimeConfig?.NEXT_PUBLIC_ALGOLIA_INDEX_PRICE_DESC;
export const INDEX_SORT_DISCOUNT_PERCENT_DESC: string =
    publicRuntimeConfig?.NEXT_PUBLIC_ALGOLIA_INDEX_DISCOUNT_PERCENT_DESC;
export const INDEX_SORT_NEWEST_DESC: string =
    publicRuntimeConfig?.NEXT_PUBLIC_ALGOLIA_INDEX_NEWEST_DESC;
export const INDEX_SORT_RATINGS_DESC: string =
    publicRuntimeConfig?.NEXT_PUBLIC_ALGOLIA_INDEX_RATINGS_DESC;
export const ALGOLIA_QUERY_SUGGESTIONS: string =
    publicRuntimeConfig?.NEXT_PUBLIC_ALGOLIA_QUERY_SUGGESTIONS;
/**
 * algoliasearch/lite client, doesn’t offer indexing methods.
 * If you want to perform indexing operations,
 * you have to import the regular algoliasearch client.
 */
export const algoliaClient = algoliasearch(ALGOLIA_APP, ALGOLIA_TOKEN);

export type TALGOLIA_INDICES =
    | 'MAIN'
    | 'PRICE_ASC'
    | 'PRICE_DESC'
    | 'DISCOUNT_PERCENT_DESC'
    | 'NEWEST_DESC'
    | 'RATINGS_DESC'
    | 'QUERY_SUGGESTIONS';

export const getAlgoliaIndex = (key: TALGOLIA_INDICES) => {
    switch (key) {
        case 'MAIN':
            return ALGOLIA_INDEX;
        case 'PRICE_ASC':
            return INDEX_SORT_PRICE_ASC;
        case 'PRICE_DESC':
            return INDEX_SORT_PRICE_DESC;
        case 'DISCOUNT_PERCENT_DESC':
            return INDEX_SORT_DISCOUNT_PERCENT_DESC;
        case 'RATINGS_DESC':
            return INDEX_SORT_RATINGS_DESC;
        case 'NEWEST_DESC':
            return INDEX_SORT_NEWEST_DESC;
        case 'QUERY_SUGGESTIONS':
            return ALGOLIA_QUERY_SUGGESTIONS;
        default:
            return ALGOLIA_INDEX;
    }
};

export const MainAlgoliaIndex = getAlgoliaIndex('MAIN');

export type AlgoliaSortOption = {
    value: string;
    label: TranslationKey;
};

export const algoliaSortOptions: AlgoliaSortOption[] = [
    {
        label: 'plp.sortPopular',
        value: getAlgoliaIndex('MAIN'),
    },
    {
        label: 'plp.sortPriceAsc',
        value: getAlgoliaIndex('PRICE_ASC'),
    },
    {
        label: 'plp.sortPriceDesc',
        value: getAlgoliaIndex('PRICE_DESC'),
    },
    {
        label: 'plp.sortDiscountPercentDesc',
        value: getAlgoliaIndex('DISCOUNT_PERCENT_DESC'),
    },
    {
        label: 'plp.sortNewestDesc',
        value: getAlgoliaIndex('NEWEST_DESC'),
    },
    {
        label: 'plp.sortRatingsDesc',
        value: getAlgoliaIndex('RATINGS_DESC'),
    },
];

export const useAlgoliaSortOptions = () => {
    const { data: frame } = useFrame();

    const options = useMemo(() => {
        /**
         * We will not include 'RATINGS_DESC' if and only if showSortByRatings is false.
         * This means that it will show if showSortByRatings is true or unimplemented.
         */
        if (frame.productSettings?.showSortByRatings === false) {
            return algoliaSortOptions.filter(
                (option) => option.value !== getAlgoliaIndex('RATINGS_DESC'),
            );
        }

        return algoliaSortOptions;
    }, [frame.productSettings?.showSortByRatings]);

    return options;
};

export const createURL = (state: any) => `?${qs.stringify(state)}`;

export const pathToSearchState = (path: any) =>
    path.includes('?')
        ? qs.parse(path.substring(path.indexOf('?') + 1), {
              //   comma: true,
          })
        : {};

export const searchStateToURL = (searchState: any) => {
    if (searchState) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { configure, productids, prevPath, sortBy, slug, ...rest } = searchState;
        const params = {
            ...rest,
            productids,
        };
        return `${window.location.pathname}?${qs.stringify(params, {
            skipNulls: true,
        })}`;
    }
    return '';
};
